import React, { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight } from "lucide-react";
import useArchiveNeighbors from "../hooks/useArchiveNeighbors";

/**
 * Inline prev/next navigation strip for dossier-style pages.
 * - Type-aware: stays within its lane (curated / AI / connection).
 * - Keyboard arrow keys ←/→ also navigate.
 * - Renders nothing if both neighbors are null (single-item archive edge case).
 *
 * Props:
 * - type: "dossier" | "ai_dossier" | "connection"
 * - currentId: slug or id of the current file
 * - urlBuilder: (id) => path, e.g. (slug) => `/dossier/${slug}`
 * - accent (optional): hex color for hover; defaults to red
 * - testIdPrefix (optional)
 */
export default function PrevNextNav({ type, currentId, urlBuilder, accent = "#ff3b30", testIdPrefix = "navstrip" }) {
  const { prev, next, list, index } = useArchiveNeighbors(type, currentId);
  const navigate = useNavigate();

  useEffect(() => {
    const onKey = (e) => {
      // Skip if user is typing
      const tag = (e.target?.tagName || "").toLowerCase();
      if (tag === "input" || tag === "textarea" || e.target?.isContentEditable) return;
      if (e.metaKey || e.ctrlKey || e.altKey || e.shiftKey) return;
      if (e.key === "ArrowLeft" && prev) {
        navigate(urlBuilder(prev.id));
      } else if (e.key === "ArrowRight" && next) {
        navigate(urlBuilder(next.id));
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [prev, next, navigate, urlBuilder]);

  if (!prev && !next) return null;

  const accentStyle = { color: accent, borderColor: `${accent}66` };

  return (
    <nav
      data-testid={`${testIdPrefix}-bar`}
      aria-label="Archive navigation"
      className="flex items-stretch justify-between gap-3 my-8 border-y border-white/10 py-4"
    >
      {/* Prev */}
      {prev ? (
        <Link
          to={urlBuilder(prev.id)}
          data-testid={`${testIdPrefix}-prev`}
          className="group flex items-center gap-3 flex-1 min-w-0 px-4 py-3 border border-white/10 hover:border-current transition-colors"
          style={accentStyle}
          title="Previous file (or press ←)"
        >
          <ChevronLeft size={18} className="shrink-0 group-hover:-translate-x-0.5 transition-transform" />
          <div className="min-w-0 flex-1">
            <div className="font-mono text-[9px] tracking-[0.25em] uppercase text-zinc-500 mb-0.5">
              ← Prev
            </div>
            <div className="font-heading uppercase tracking-tight text-sm text-white truncate">
              {prev.title}
            </div>
          </div>
        </Link>
      ) : (
        <div className="flex-1 px-4 py-3 border border-dashed border-white/10 font-mono text-[10px] tracking-[0.2em] uppercase text-zinc-600 inline-flex items-center">
          Start of {laneLabel(type)}
        </div>
      )}

      {/* Position indicator */}
      <div className="hidden sm:flex items-center px-3 font-mono text-[10px] tracking-[0.2em] text-zinc-500 shrink-0 self-center">
        {index >= 0 && list.length > 0
          ? `${index + 1} / ${list.length}`
          : ""}
      </div>

      {/* Next */}
      {next ? (
        <Link
          to={urlBuilder(next.id)}
          data-testid={`${testIdPrefix}-next`}
          className="group flex items-center gap-3 flex-1 min-w-0 px-4 py-3 border border-white/10 hover:border-current transition-colors text-right"
          style={accentStyle}
          title="Next file (or press →)"
        >
          <div className="min-w-0 flex-1">
            <div className="font-mono text-[9px] tracking-[0.25em] uppercase text-zinc-500 mb-0.5">
              Next →
            </div>
            <div className="font-heading uppercase tracking-tight text-sm text-white truncate">
              {next.title}
            </div>
          </div>
          <ChevronRight size={18} className="shrink-0 group-hover:translate-x-0.5 transition-transform" />
        </Link>
      ) : (
        <div className="flex-1 px-4 py-3 border border-dashed border-white/10 font-mono text-[10px] tracking-[0.2em] uppercase text-zinc-600 text-right inline-flex items-center justify-end">
          End of {laneLabel(type)}
        </div>
      )}
    </nav>
  );
}

function laneLabel(type) {
  switch (type) {
    case "dossier":
      return "Curated";
    case "ai_dossier":
      return "AI Dives";
    case "connection":
      return "Connections";
    default:
      return "Archive";
  }
}
